import { Component, computed, inject, input, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import {
  FormField,
  FormRoot,
  email,
  form,
  required,
  submit,
} from '@angular/forms/signals';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { isBrowser } from '@mb/common/client';
import { HlmFieldImports } from '@mb/ui/field';
import { HlmInputImports } from '@mb/ui/input';
import type { PermissionAction } from '@mb/auth/shared';
import type { IPerson } from '@modules/contacts/shared';
import { ORGANIZATION_ENTITY, PERSON_ACTION } from '../../shared';
import { OrganizationContextStore } from '../state/organization-context.state';
import { OrganizationPersonApiService } from '../services/organization-person-api.service';

interface OrgPersonFormModel {
  firstName: string;
  lastName: string;
  email: string;
  isOwner: boolean;
}

const EMPTY: OrgPersonFormModel = {
  firstName: '',
  lastName: '',
  email: '',
  isOwner: false,
};

@Component({
  selector: 'workforce-organization-persons',
  standalone: true,
  imports: [
    FormField,
    FormRoot,
    HlmFieldImports,
    HlmInputImports,
    TranslocoPipe,
  ],
  template: `
    <section class="space-y-3">
      <h3 class="text-base font-semibold text-foreground">
        {{ 'organization.persons.title' | transloco }}
      </h3>

      <form
        [formRoot]="personForm"
        class="grid gap-3 rounded-xl border border-border bg-background p-4 sm:grid-cols-2"
      >
        <input
          hlmInput
          class="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
          [formField]="personForm.firstName"
          [placeholder]="'organization.persons.fields.firstName' | transloco"
        />
        <input
          hlmInput
          class="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
          [formField]="personForm.lastName"
          [placeholder]="'organization.persons.fields.lastName' | transloco"
        />
        <input
          hlmInput
          type="email"
          class="h-9 w-full rounded-md border border-input bg-background px-3 text-sm sm:col-span-2"
          [formField]="personForm.email"
          [placeholder]="'organization.persons.fields.email' | transloco"
        />
        <label
          class="flex items-center gap-2 text-sm text-foreground sm:col-span-2"
        >
          <input
            type="checkbox"
            class="size-4 rounded border-input text-primary"
            [formField]="personForm.isOwner"
          />
          {{ 'organization.persons.fields.isOwner' | transloco }}
        </label>
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 sm:col-span-2"
          [disabled]="creating() || personForm().invalid()"
          (click)="submitForm()"
        >
          {{
            (creating()
              ? 'organization.persons.actions.creating'
              : 'organization.persons.actions.addAndInvite'
            ) | transloco
          }}
        </button>
      </form>

      @if (lastInvite(); as invite) {
        <div
          class="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
        >
          {{
            'organization.persons.inviteSent'
              | transloco: { email: invite.email }
          }}
          @if (invite.url) {
            <span class="break-all text-emerald-700">
              · {{ 'organization.persons.inviteLink' | transloco }}:
              {{ invite.url }}
            </span>
          }
        </div>
      }
      @if (error()) {
        <div class="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {{ error() }}
        </div>
      }

      <ul
        class="divide-y divide-border overflow-hidden rounded-xl border border-border bg-background"
      >
        @for (person of persons(); track person.id) {
          <li class="flex items-center justify-between gap-3 px-4 py-2 text-sm">
            <div class="min-w-0">
              <span class="text-foreground"
                >{{ person.firstName }} {{ person.lastName }}</span
              >
              <span class="text-muted-foreground">
                · {{ person.email
                }}{{
                  person.userId
                    ? ('organization.persons.status.linked' | transloco)
                    : ('organization.persons.status.invited' | transloco)
                }}
              </span>
            </div>
            <div class="flex shrink-0 items-center gap-1">
              @if (!person.userId && canSendInvite()) {
                <button
                  type="button"
                  class="rounded-md px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10 disabled:opacity-50"
                  [disabled]="busyId() === person.id"
                  (click)="resend(person)"
                >
                  {{ 'organization.persons.actions.resendInvite' | transloco }}
                </button>
              }
              <button
                type="button"
                class="rounded-md px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                [disabled]="busyId() === person.id"
                (click)="remove(person)"
              >
                {{ 'common.actions.delete' | transloco }}
              </button>
            </div>
          </li>
        } @empty {
          <li class="px-4 py-3 text-sm text-muted-foreground">
            @if (personsResource.isLoading()) {
              {{ 'organization.persons.loading' | transloco }}
            } @else {
              {{ 'organization.persons.empty' | transloco }}
            }
          </li>
        }
      </ul>
    </section>
  `,
})
export class OrganizationPersonsComponent {
  readonly organizationId = input.required<string>();
  readonly #api = inject(OrganizationPersonApiService);
  readonly #transloco = inject(TranslocoService);
  readonly #orgContext = inject(OrganizationContextStore);

  readonly canSendInvite = computed(() =>
    this.#orgContext.can(
      ORGANIZATION_ENTITY.PERSON,
      PERSON_ACTION.SEND_INVITE as PermissionAction,
    ),
  );

  readonly personsResource = httpResource<IPerson[]>(() => {
    const id = this.organizationId();
    if (!id || !isBrowser()) return undefined;
    return {
      url: `/api/v1/organization/organizations/${id}/persons`,
      method: 'GET',
      withCredentials: true,
    };
  });
  readonly persons = computed(() => this.personsResource.value() ?? []);

  readonly creating = signal(false);
  readonly busyId = signal<string | null>(null);
  readonly error = signal<string | null>(null);
  readonly lastInvite = signal<{ email: string; url: string | null } | null>(
    null,
  );

  readonly model = signal<OrgPersonFormModel>({ ...EMPTY });
  readonly personForm = form(this.model, (schema) => {
    required(schema.firstName);
    required(schema.lastName);
    required(schema.email);
    email(schema.email);
  });

  async resend(person: IPerson): Promise<void> {
    this.error.set(null);
    this.busyId.set(person.id);
    try {
      const res = await this.#api.resendInvite(
        this.organizationId(),
        person.id,
      );
      this.lastInvite.set({ email: person.email, url: res.data.inviteUrl });
    } catch {
      this.error.set(
        this.#transloco.translate('organization.persons.errors.resendFailed'),
      );
    } finally {
      this.busyId.set(null);
    }
  }

  async remove(person: IPerson): Promise<void> {
    const confirmMessage = this.#transloco.translate(
      'organization.persons.confirmRemove',
      {
        name: `${person.firstName} ${person.lastName}`,
      },
    );
    if (isBrowser() && !confirm(confirmMessage)) {
      return;
    }
    this.error.set(null);
    this.busyId.set(person.id);
    try {
      await this.#api.delete(this.organizationId(), person.id);
      this.personsResource.reload();
    } catch {
      this.error.set(
        this.#transloco.translate('organization.persons.errors.removeFailed'),
      );
    } finally {
      this.busyId.set(null);
    }
  }

  submitForm(): void {
    submit(this.personForm, async () => {
      const organizationId = this.organizationId();
      this.creating.set(true);
      this.error.set(null);
      try {
        const value = this.model();
        const res = await this.#api.create(organizationId, {
          firstName: value.firstName.trim(),
          lastName: value.lastName.trim(),
          email: value.email.trim(),
          type: 'employee',
          isOwner: value.isOwner,
        });
        this.lastInvite.set({
          email: res.data.person.email,
          url: res.data.inviteUrl,
        });
        this.model.set({ ...EMPTY });
        this.personsResource.reload();
      } catch {
        this.error.set(
          this.#transloco.translate('organization.persons.errors.createFailed'),
        );
      } finally {
        this.creating.set(false);
      }
    });
  }
}
